import type { Href } from 'expo-router';
import { Link } from 'expo-router';
import { type LucideIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Pressable, View } from 'react-native';

import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { UserMenu } from '@/components/user-menu';
import { cn } from '@sixam/ui/utils';

export type SidebarItem = {
  label: string;
  icon: LucideIcon;
  href: Href;
  active: boolean;
};

/** Persistent left navigation for web and wide native layouts. */
export function DashboardSidebar({
  brand,
  headerAccessory,
  subtitle,
  items,
  footer,
  userMenu,
}: Readonly<{
  brand: string;
  headerAccessory?: ReactNode;
  subtitle: string;
  items: readonly SidebarItem[];
  footer?: ReactNode;
  userMenu?: ReactNode;
}>) {
  return (
    <View className="w-64 shrink-0 border-r border-border bg-background">
      <View className="h-full gap-1 p-3">
        <View className="px-2 py-3">
          <Text className="text-lg font-semibold leading-tight" numberOfLines={1}>
            {brand}
          </Text>
          <Text variant="muted" className="text-xs">
            {subtitle}
          </Text>
        </View>
        {headerAccessory ? <View className="px-1 pb-2">{headerAccessory}</View> : null}

        {items.map((item) => (
          <SidebarLink key={item.label} {...item} />
        ))}

        <View className="mt-auto flex-row items-center gap-3 border-t border-border px-1 pt-3">
          {userMenu ?? <UserMenu />}
          {footer}
        </View>
      </View>
    </View>
  );
}

function SidebarLink({ label, icon, href, active }: Readonly<SidebarItem>) {
  return (
    <Link href={href} asChild>
      <Pressable
        className={cn(
          'group flex-row items-center gap-3 rounded-md px-3 py-2',
          active ? 'bg-accent' : 'hover:bg-accent/50'
        )}
      >
        <Icon
          as={icon}
          className={cn('size-4', active ? 'text-accent-foreground' : 'text-muted-foreground')}
        />
        <Text
          className={cn(
            'text-sm font-medium',
            active ? 'text-accent-foreground' : 'text-muted-foreground'
          )}
        >
          {label}
        </Text>
      </Pressable>
    </Link>
  );
}
